import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Clock, Rocket, XCircle } from "lucide-react";
import { formatPrice } from "@nh/shared";
import { Button } from "@/shared/ui/core";
import { useAuth } from "@/features/auth/auth.context";
import { CartProvider } from "@/features/cart";
import { ShopLayout } from "@/features/shop/components/ShopLayout";
import { useShopCurrency } from "@/features/shop/useShopCurrency";
import { useMyOrders } from "@/features/mi-negocio/minegocio.api";
import { OrderStatusBadge } from "@/features/admin/orders/OrderStatusBadge";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El REGRESO del pago — a donde Stripe devuelve el navegador.
 *
 * Antes volvía a `/shop` y ahí no pasaba nada (Anahí, 27-ago: "me
 * redirigió a /shop cuando ya debe mandarme a mi franquicia o a mi
 * panel"). Esta pantalla reconoce el folio y dice la verdad de cada
 * momento:
 *
 *  · Pago hecho, orden PENDIENTE → "recibimos tu pago, lo estamos
 *    validando" — porque la validación es manual, y prometer más sería
 *    mentir.
 *  · Orden de FRANQUICIA ya validada → la puerta grande al panel de
 *    franquiciatario, que es a donde ella quería llegar.
 *  · Pago cancelado → el pedido sigue registrado; se dice cómo seguir.
 *
 * "Verificar de nuevo" refresca la sesión Y el pedido: es lo que
 * convierte esta pantalla en la sala de espera de la validación.
 * ═══════════════════════════════════════════════════════════════════
 */
export function OrderReturnPage() {
  return (
    <CartProvider>
      <OrderReturnScreen />
    </CartProvider>
  );
}

function OrderReturnScreen() {
  const { t } = useTranslation();
  const [currency, setCurrency] = useShopCurrency();
  const [params] = useSearchParams();
  const location = useLocation();
  const { user, isAuthenticated, restore } = useAuth();

  const folio = params.get("pedido");
  const cancelled = params.get("pago") === "cancelado";

  /*
   * Se busca el folio entre MIS pedidos: no hay endpoint público de
   * órdenes por folio, y no debe haberlo — un folio adivinado no puede
   * enseñar el pedido de otra persona.
   */
  const orders = useMyOrders({ page: 1, perPage: 50, role: "buyer" });
  const order = folio
    ? orders.data?.items.find((item) => item.folio === folio)
    : undefined;

  if (!isAuthenticated) {
    return (
      <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
    );
  }

  const esFranquicia = Boolean(order?.isFranchise);
  const yaFranquiciataria = Boolean(user?.franchiseeSince);
  const validada = Boolean(order?.purchasedAt);

  async function verificar() {
    // La sesión trae `franchiseeSince`; el pedido trae su estado. Los
    // dos pueden haber cambiado desde que se pintó esta pantalla.
    await Promise.all([restore(), orders.refetch()]);
  }

  return (
    <ShopLayout currency={currency} onCurrencyChange={setCurrency}>
      <div className="mx-auto w-full max-w-xl px-4 py-12 text-center sm:px-8">
        {cancelled ? (
          <>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ds-warn-bg text-ds-warn-t">
              <XCircle className="h-7 w-7" />
            </span>
            <h1 className="mt-5 text-[26px] font-semibold text-ds-ink">
              {t("El pago se canceló")}
            </h1>
            <p className="mt-2 text-[15px] leading-relaxed text-ds-ink-3">
              {folio
                ? t(
                    "Tu pedido {{folio}} sigue registrado. Puedes pagarlo por transferencia y enviar tu comprobante, o intentar de nuevo desde donde lo creaste.",
                    { folio },
                  )
                : t("No se hizo ningún cargo.")}
            </p>
          </>
        ) : esFranquicia && yaFranquiciataria ? (
          <>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ds-ok-bg text-ds-ok-t">
              <Rocket className="h-7 w-7" />
            </span>
            <h1 className="mt-5 text-[26px] font-semibold text-ds-ink">
              {t("¡Tu Franquicia Digital está activa!")}
            </h1>
            <p className="mt-2 text-[15px] leading-relaxed text-ds-ink-3">
              {t("Tu pago quedó validado. Tu tienda y tu panel te esperan.")}
            </p>
            <Button as={Link} to="/mi-negocio" size="lg" className="mt-6">
              {t("Entrar a mi panel de franquiciataria")}
            </Button>
          </>
        ) : (
          <>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ds-ok-bg text-ds-ok-t">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <h1 className="mt-5 text-[26px] font-semibold text-ds-ink">
              {t("¡Recibimos tu pago!")}
            </h1>
            {folio && (
              <p className="mt-4 inline-block rounded-2xl border border-ds-border bg-white px-6 py-3 font-mono text-[18px] font-semibold text-ds-ink shadow-ds-float">
                {folio}
              </p>
            )}
            {order && (
              <p className="mt-3 flex items-center justify-center gap-2 text-[14px] text-ds-ink-3">
                {formatPrice(order.totalCents, order.currency)} ·{" "}
                <OrderStatusBadge status={order.status} />
              </p>
            )}

            <div className="mx-auto mt-6 flex max-w-md items-start gap-3 rounded-2xl border border-ds-border bg-white p-4 text-left">
              <Clock className="mt-0.5 h-5 w-5 shrink-0 text-ds-ink-3" />
              <p className="text-[13px] leading-relaxed text-ds-ink-3">
                {esFranquicia
                  ? t(
                      "Estamos validando tu pago. En cuanto se apruebe, tu Franquicia Digital se activa y AQUÍ se abre tu panel de franquiciataria.",
                    )
                  : t(
                      "Estamos validando tu pago. En cuanto se apruebe, tu pedido cambiará de estado.",
                    )}
              </p>
            </div>

            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                loading={orders.isFetching}
                onClick={() => void verificar()}
              >
                {t("Verificar de nuevo")}
              </Button>
              <Button as={Link} to="/shop" variant="ghost" size="sm">
                {t("Seguir comprando")}
              </Button>
            </div>
            {validada && !esFranquicia && (
              <p className="mt-4 text-[13px] text-ds-ink-3">
                {t("¡Listo! Tu pedido ya está validado.")}
              </p>
            )}
          </>
        )}
      </div>
    </ShopLayout>
  );
}
